import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import Form from "react-bootstrap/Form";
import { useEffect, useMemo, useRef, useState } from "react";
import axiosClient from "../../axiosClient";
import { useNavigate } from "react-router";
import { Assignment, Module } from "../../types";
import {
  getTypesPresentInSubject,
  PracticeQuestionType,
  practiceQuestionTypeLabel,
  sumQuestionTypeTotals,
} from "../../utils/practiceQuestionTypes";
import toast from "react-hot-toast";

const DEFAULT_QUESTION_COUNT = 90;
const DEFAULT_DURATION_MINUTES = 120;
/** Hard caps — must match backend timed-practice limits. */
const MAX_TIMED_PRACTICE_QUESTIONS = 120;
const MIN_TIMED_PRACTICE_DURATION_MINUTES = 1;
const MAX_TIMED_PRACTICE_DURATION_MINUTES = 120;
const MAX_TIMED_PRACTICE_NAME_LENGTH = 100;
const NAME_PLACEHOLDER = "e.g. Mock Part 1 paper";
/** Ignore Start clicks that are part of the same gesture that opened the modal. */
const OPEN_CLICK_GUARD_MS = 400;

function sumEmqGroups(modules: Module[]): number {
  return modules.reduce(
    (sum, mod) => sum + (Number(mod.numberOfEmqGroups) || 0),
    0,
  );
}

function availableCountCap(available: number): number {
  return Math.min(MAX_TIMED_PRACTICE_QUESTIONS, Math.max(0, available));
}

function TestExamModal(props: {
  showModal: boolean;
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
  subjectId: number;
  modules: Module[];
}) {
  const handleClose = () => {
    setIsSubmitting(false);
    props.setShowModal(false);
  };
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const openedAtRef = useRef(0);

  const availableTypes = useMemo(
    () => getTypesPresentInSubject(props.modules),
    [props.modules],
  );
  const totalsByType = useMemo(
    () => sumQuestionTypeTotals(props.modules),
    [props.modules],
  );
  const emqGroupTotal = useMemo(
    () => sumEmqGroups(props.modules),
    [props.modules],
  );
  const showTypePicker = availableTypes.length >= 2;

  const [selectedType, setSelectedType] = useState<PracticeQuestionType | null>(
    null,
  );
  const [sessionName, setSessionName] = useState("");
  const [countInput, setCountInput] = useState(String(DEFAULT_QUESTION_COUNT));
  const [durationMinutes, setDurationMinutes] = useState(
    String(DEFAULT_DURATION_MINUTES),
  );

  // Prefer explicit selection; otherwise the only available type; otherwise MCQ fallback
  // so Start never silently no-ops when type inventory is missing.
  const effectiveType: PracticeQuestionType | null = showTypePicker
    ? selectedType
    : availableTypes[0] ?? (props.modules.some((m) => m.numberOfQuestions > 0)
        ? "MCQ"
        : null);

  const isEmq = effectiveType === "EMQ";
  const inventoryCount = isEmq
    ? emqGroupTotal
    : effectiveType
      ? totalsByType[effectiveType] ||
        props.modules.reduce((sum, m) => sum + (m.numberOfQuestions || 0), 0)
      : props.modules.reduce((sum, m) => sum + (m.numberOfQuestions || 0), 0);
  const maxCount = availableCountCap(inventoryCount);

  const emqPartsAvailable = totalsByType.EMQ;

  useEffect(() => {
    if (!props.showModal) {
      return;
    }

    openedAtRef.current = Date.now();

    const nextType = availableTypes[0] ?? null;
    setSelectedType(showTypePicker ? nextType : null);

    const nextInventory =
      nextType === "EMQ"
        ? emqGroupTotal
        : nextType
          ? totalsByType[nextType] ||
            props.modules.reduce((sum, m) => sum + (m.numberOfQuestions || 0), 0)
          : props.modules.reduce((sum, m) => sum + (m.numberOfQuestions || 0), 0);
    const nextMax = availableCountCap(nextInventory);

    setSessionName("");
    setCountInput(
      String(Math.min(DEFAULT_QUESTION_COUNT, Math.max(1, nextMax || 1))),
    );
    setDurationMinutes(String(DEFAULT_DURATION_MINUTES));
    setIsSubmitting(false);
  }, [props.showModal]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!props.showModal || !effectiveType) return;
    const nextInventory =
      effectiveType === "EMQ"
        ? emqGroupTotal
        : totalsByType[effectiveType] || inventoryCount;
    const nextMax = availableCountCap(nextInventory);
    const parsed = Number(countInput);
    if (!Number.isFinite(parsed) || parsed < 1 || parsed > nextMax) {
      setCountInput(
        String(Math.min(DEFAULT_QUESTION_COUNT, Math.max(1, nextMax || 1))),
      );
    }
  }, [effectiveType]); // eslint-disable-line react-hooks/exhaustive-deps

  const count = Number(countInput);
  const duration = Number(durationMinutes);
  const trimmedName = sessionName.trim();
  const nameValid =
    trimmedName.length >= 1 && trimmedName.length <= MAX_TIMED_PRACTICE_NAME_LENGTH;
  const countValid =
    Number.isFinite(count) && count >= 1 && count <= maxCount && maxCount > 0;
  const durationValid =
    Number.isFinite(duration) &&
    duration >= MIN_TIMED_PRACTICE_DURATION_MINUTES &&
    duration <= MAX_TIMED_PRACTICE_DURATION_MINUTES;
  const typeValid =
    effectiveType != null && (!showTypePicker || selectedType != null);
  const formReady = nameValid && countValid && durationValid && typeValid;
  const canStart = formReady && !isSubmitting;

  const selectedPartsEstimate =
    isEmq && emqGroupTotal > 0
      ? Math.round((emqPartsAvailable / emqGroupTotal) * count)
      : null;

  const startExam = async () => {
    // Swallow the open-gesture click-through without disabling the button long-term
    if (Date.now() - openedAtRef.current < OPEN_CLICK_GUARD_MS) {
      return;
    }

    if (!formReady || !effectiveType) {
      toast.error(
        !nameValid
          ? "Enter a name for this timed practice session."
          : !typeValid
            ? "Choose a question type to continue."
            : !countValid
              ? `Enter between 1 and ${Math.max(1, maxCount)} ${isEmq ? "groups" : "questions"}.`
              : `Enter a duration between ${MIN_TIMED_PRACTICE_DURATION_MINUTES} and ${MAX_TIMED_PRACTICE_DURATION_MINUTES} minutes.`,
      );
      return;
    }

    setIsSubmitting(true);
    try {
      const testExam: {
        assignment: Assignment;
        submission: {
          id: number;
          submittedDate: Date | null;
          assignmentId: number;
          studentId: number;
          score: number | null;
        };
      } = (
        await axiosClient.post(`/assignments-practice`, {
          subjectId: props.subjectId,
          name: trimmedName,
          selectedModules: {
            "module-all": count,
          },
          examDurationSeconds: Math.round(duration * 60),
          questionType: effectiveType,
        })
      ).data;

      props.setShowModal(false);
      window.setTimeout(() => {
        navigate("/assignment", {
          state: {
            submissionId: testExam.submission.id,
            assignmentId: testExam.assignment.id,
            assignmentName: testExam.assignment.name,
          },
        });
      }, 0);
    } catch (err: unknown) {
      console.error(err);
      const status = (err as { response?: { status?: number } })?.response?.status;
      toast.error(
        status === 429
          ? "Too many timed exams started. Please wait a few minutes and try again."
          : "Could not start timed exam. Please try again.",
      );
      setIsSubmitting(false);
    }
  };

  return (
    <Modal show={props.showModal} onHide={handleClose} centered>
      <Modal.Header closeButton closeLabel="close modal">
        <Modal.Title style={{ fontSize: "1rem" }}>Timed exam</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="small text-muted mb-3">
          Timed practice always covers this whole subject — not individual
          modules. Choose how many {isEmq ? "EMQ groups" : "questions"} and how
          long the exam should last. Questions are drawn randomly from across
          all modules.
        </p>

        <Form
          id="timed-exam-form"
          onSubmit={(e) => {
            e.preventDefault();
            void startExam();
          }}
        >
          <Form.Group className="mb-3">
            <Form.Label className="small fw-semibold" htmlFor="timed-exam-name">
              Session name
            </Form.Label>
            <Form.Control
              id="timed-exam-name"
              type="text"
              value={sessionName}
              placeholder={NAME_PLACEHOLDER}
              maxLength={MAX_TIMED_PRACTICE_NAME_LENGTH}
              onChange={(e) => setSessionName(e.target.value)}
              required
              autoComplete="off"
            />
            {!nameValid && sessionName.length > 0 ? (
              <Form.Text className="text-danger">
                Name must be between 1 and {MAX_TIMED_PRACTICE_NAME_LENGTH}{" "}
                characters.
              </Form.Text>
            ) : null}
          </Form.Group>

          {showTypePicker && (
            <Form.Group className="mb-3">
              <Form.Label className="small fw-semibold mb-2">
                Question type
              </Form.Label>
              <div
                className="d-grid gap-2"
                role="radiogroup"
                aria-label="Question type for timed practice"
              >
                {availableTypes.map((type) => {
                  const isSelected = selectedType === type;
                  const typeInventory =
                    type === "EMQ" ? emqGroupTotal : totalsByType[type];
                  const typeMax = availableCountCap(typeInventory);
                  const meta =
                    type === "EMQ"
                      ? `${emqGroupTotal} groups (${totalsByType.EMQ} parts)`
                      : `${typeMax} available`;
                  return (
                    <button
                      key={type}
                      type="button"
                      role="radio"
                      aria-checked={isSelected}
                      onClick={() => setSelectedType(type)}
                      className="text-start border rounded"
                      style={{
                        backgroundColor: isSelected ? "rgba(var(--brand-accent-rgb), 0.06)" : "#fafafa",
                        borderColor: isSelected ? "var(--brand-accent)" : undefined,
                        padding: 10,
                      }}
                    >
                      <div className="d-flex justify-content-between gap-2">
                        <span className="small fw-semibold">
                          {practiceQuestionTypeLabel(type)}
                        </span>
                        <span className="small text-muted">{meta}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </Form.Group>
          )}

          <Form.Group className="mb-3">
            <Form.Label className="small fw-semibold" htmlFor="timed-exam-count">
              {isEmq ? "Number of EMQ groups" : "Number of questions"}
            </Form.Label>
            <Form.Control
              id="timed-exam-count"
              type="number"
              min={1}
              max={Math.max(1, maxCount)}
              value={countInput}
              onChange={(e) => setCountInput(e.target.value)}
              disabled={maxCount < 1}
            />
            <Form.Text muted>
              {isEmq ? (
                <>
                  {emqGroupTotal} groups available ({emqPartsAvailable} parts)
                  {emqGroupTotal > MAX_TIMED_PRACTICE_QUESTIONS
                    ? ` (max ${MAX_TIMED_PRACTICE_QUESTIONS} groups per exam)`
                    : ""}
                  .
                  {countValid && selectedPartsEstimate != null
                    ? ` Selected: ${count} groups · ~${selectedPartsEstimate} parts.`
                    : null}
                </>
              ) : (
                <>
                  {inventoryCount} available for this type
                  {inventoryCount > MAX_TIMED_PRACTICE_QUESTIONS
                    ? ` (max ${MAX_TIMED_PRACTICE_QUESTIONS} per exam)`
                    : ""}
                  .
                </>
              )}
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-0">
            <Form.Label
              className="small fw-semibold"
              htmlFor="timed-exam-duration"
            >
              Duration (minutes)
            </Form.Label>
            <Form.Control
              id="timed-exam-duration"
              type="number"
              min={MIN_TIMED_PRACTICE_DURATION_MINUTES}
              max={MAX_TIMED_PRACTICE_DURATION_MINUTES}
              value={durationMinutes}
              onChange={(e) => setDurationMinutes(e.target.value)}
            />
            {durationValid ? (
              <Form.Text muted>
                {duration >= 60
                  ? `${Math.floor(duration / 60)}h ${duration % 60}m total`
                  : `${duration} minute${duration === 1 ? "" : "s"} total`}
                {` (max ${MAX_TIMED_PRACTICE_DURATION_MINUTES} min)`}
              </Form.Text>
            ) : (
              <Form.Text className="text-danger">
                Enter between {MIN_TIMED_PRACTICE_DURATION_MINUTES} and{" "}
                {MAX_TIMED_PRACTICE_DURATION_MINUTES} minutes.
              </Form.Text>
            )}
          </Form.Group>
        </Form>

        {maxCount < 1 && (
          <p className="small text-danger mt-3 mb-0">
            This subject does not have enough{" "}
            {isEmq ? "EMQ groups" : "questions"} yet.
          </p>
        )}
        {maxCount >= 1 && !countValid && (
          <p className="small text-danger mt-3 mb-0">
            Enter a number between 1 and {maxCount}.
          </p>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="danger" size="sm" type="button" onClick={handleClose}>
          Close
        </Button>
        <Button
          variant="dark"
          size="sm"
          type="button"
          disabled={!canStart}
          onClick={() => void startExam()}
        >
          {isSubmitting ? "Starting…" : "Start exam"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default TestExamModal;
